import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CreditCard, Calendar, ShoppingBag } from 'lucide-react';
import type { GrSubscriptionResponse } from '@/lib/api';

interface SubscriptionCardProps {
  subscription: GrSubscriptionResponse | null;
}

const tierVariantMap: Record<string, 'default' | 'secondary' | 'outline'> = {
  bronze: 'outline',
  silver: 'secondary',
  gold: 'default',
};

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function SubscriptionCard({ subscription }: SubscriptionCardProps) {
  if (!subscription) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <CreditCard className="h-4 w-4" /> Subscription
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No active subscription</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <CreditCard className="h-4 w-4" /> Subscription
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Tier</span>
          <Badge variant={tierVariantMap[subscription.tier] ?? 'outline'}>
            {subscription.tier.charAt(0).toUpperCase() + subscription.tier.slice(1)}
          </Badge>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground flex items-center gap-1">
            <Calendar className="h-3 w-3" /> Expires
          </span>
          <span className="text-sm">{formatDate(subscription.expiresAt)}</span>
        </div>
        {subscription.purchasedVia && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground flex items-center gap-1">
              <ShoppingBag className="h-3 w-3" /> Purchased via
            </span>
            <span className="text-sm">{subscription.purchasedVia}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
